import { z } from 'zod';
import { ADMIN_API_KEY_GRANTABLE_PERMISSIONS, type Permission } from '../permissions';
import { IncludeTestWorkspacesQuerySchema } from './test-workspaces.schemas';

// Admin operator read surface over the API-keys resource tree (IMM-414.2).
// Read-only, gated by the `backoffice:identity:api-key-read` permission. Two
// levels: the keys themselves (list + detail, unpaginated — keys are
// low-cardinality) and, nested under each key, its inbound request trail (list
// keyset-paginated + per-request detail). The request list items deliberately
// omit the request/response bodies (size + PII minimisation); only the
// per-request detail endpoint returns them.

// --- Keys ------------------------------------------------------------------

// The STORED status column — what the list filter matches against. Rows carry
// the derived status below instead, which adds `expired` (IMM-601.1).
export const AdminApiKeyStatusSchema = z.enum(['active', 'revoked']);
export type AdminApiKeyStatus = z.infer<typeof AdminApiKeyStatusSchema>;

// Row status as presented: derived at read time as revoked > expired > active
// (IMM-601.1). `expired` is never stored — it falls out of `expiresAt` having
// passed — which is what makes an expired key refuse "without any action by
// anyone".
export const AdminApiKeyDerivedStatusSchema = z.enum(['active', 'revoked', 'expired']);
export type AdminApiKeyDerivedStatus = z.infer<typeof AdminApiKeyDerivedStatusSchema>;

// The two kinds of `api_keys` row (IMM-556.1). A `workspace` key acts as a
// member inside one workspace; a `service` key is bound to an immoOS operator
// instead, is scoped to workspaces explicitly, and carries no granted
// permissions of its own. `kind` is what tells a reader which of the mutually
// exclusive binding column sets is populated.
export const ApiKeyKindSchema = z.enum(['workspace', 'admin']);
export type ApiKeyKind = z.infer<typeof ApiKeyKindSchema>;

// Discriminated so "all workspaces" is a value the operator typed, never an
// empty list that could also mean "none chosen yet" (BR 4).
export const AdminKeyScopeSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('ALL') }),
  z.object({ kind: z.literal('SELECTED'), workspaceIds: z.array(z.uuid()).min(1) }),
]);
export type AdminKeyScope = z.infer<typeof AdminKeyScopeSchema>;

// One row on the operator key list — the key's own columns plus the LEFT-joined
// friendly labels. `workspaceName` / `memberEmail` are null when the referenced
// workspace / member (via its user) was since deleted or never resolved. The
// secret hash is never projected.
//
// `workspaceId` / `memberId` are null for every admin API key and `adminId` /
// `scope` are null for every workspace key — the pair is mutually exclusive and
// `kind` discriminates it. They are nullable rather than absent so one list
// renders both kinds without a per-row union.
export const AdminApiKeyListItemSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  kind: ApiKeyKindSchema,
  workspaceId: z.uuid().nullable(),
  workspaceName: z.string().nullable(),
  // Whether the key's workspace is a testing environment (IMM-593.2). Always
  // false for a key bound to no workspace — such a key is not test data.
  isTest: z.boolean(),
  memberId: z.uuid().nullable(),
  memberEmail: z.string().nullable(),
  // The immoOS operator (admin id) an admin API key is bound to and acts as
  // (IMM-556.1); null for every workspace key. Distinct from `issuedBy`, which
  // records who performed the issuance for either kind.
  adminId: z.uuid().nullable(),
  scope: AdminKeyScopeSchema.nullable(),
  // The immoOS operator (admin id) who issued the key (IMM-435); null for a
  // customer-issued key and every key predating the feature.
  issuedBy: z.uuid().nullable(),
  status: AdminApiKeyDerivedStatusSchema,
  rateLimit: z.number().int(),
  lastUsedAt: z.iso.datetime().nullable(),
  revokedAt: z.iso.datetime().nullable(),
  createdAt: z.iso.datetime(),
  // MCP-capable credential fields (IMM-601.1). `expiresAt` / `dailyCallLimit`
  // are non-null exactly when `mcpCapable` (DB CHECK for the mcp→non-null
  // direction, the domain for the converse); false/null/null for every
  // plain workspace key and every service key.
  mcpCapable: z.boolean(),
  expiresAt: z.iso.datetime().nullable(),
  dailyCallLimit: z.number().int().nullable(),
});
export type AdminApiKeyListItem = z.infer<typeof AdminApiKeyListItemSchema>;

// Detail = list item plus the key's description, granted permissions, the member
// who revoked it (null while active / admin-revoked), and the operator who
// revoked it (null while active / customer-revoked). At most one revoker is set;
// the UI derives the revoker kind (operator vs „Vom Kunden widerrufen") from
// which of the two carries a value.
export const AdminApiKeyDetailSchema = AdminApiKeyListItemSchema.extend({
  description: z.string().nullable(),
  grantedPermissions: z.array(z.string()),
  revokedBy: z.uuid().nullable(),
  revokedByAdmin: z.uuid().nullable(),
  // The workspaces a SELECTED-scope admin API key may see, with their names
  // resolved for display (IMM-556.1). Empty for an ALL-scope admin API key — whose
  // reach is every workspace, not none — and for every workspace key. Read
  // `scope` to tell those apart; this list is a display convenience, never the
  // authority on reach.
  scopedWorkspaces: z.array(z.object({ id: z.uuid(), name: z.string() })),
});
export type AdminApiKeyDetail = z.infer<typeof AdminApiKeyDetailSchema>;

// Structured filters for the key list — all optional, AND-combined.
export const ListAdminApiKeysQuerySchema = IncludeTestWorkspacesQuerySchema.extend({
  workspaceId: z.uuid().optional(),
  status: AdminApiKeyStatusSchema.optional(),
  kind: ApiKeyKindSchema.optional(),
  // The operator whose keys to list (IMM-587.6). Only an admin API key has a bound
  // operator, so this narrows to admin API keys by construction — a workspace key
  // belongs to a member, not an operator.
  adminId: z.uuid().optional(),
});
export type ListAdminApiKeysQuery = z.infer<typeof ListAdminApiKeysQuerySchema>;

// Unpaginated — keys are low-cardinality; matches the admin-organizations shape.
export const ListAdminApiKeysResponseSchema = z.object({
  data: z.array(AdminApiKeyListItemSchema),
});
export type ListAdminApiKeysResponse = z.infer<typeof ListAdminApiKeysResponseSchema>;

export const GetAdminApiKeyPathParamsSchema = z.object({
  id: z.uuid(),
});

export const GetAdminApiKeyResponseSchema = AdminApiKeyDetailSchema;
export type GetAdminApiKeyResponse = z.infer<typeof GetAdminApiKeyResponseSchema>;

// --- Admin issuance (IMM-435.1) --------------------------------------------

// The permissions an operator may attach to an admin-issued key, restricted to
// the derived admin grantable catalogue at the type level — a payload naming a
// non-member (a key-management permission, a `backoffice:*` literal, or a bogus
// identifier) is rejected at the contract boundary (400) before the domain ever
// runs the owner-holds check. The runtime value list is derived, never
// hand-maintained; the tuple cast satisfies `z.enum` (the catalogue is always
// non-empty). The inferred value type widens to `Permission`; the domain
// re-validates against the runtime catalogue, so the boundary stays exact.
export const AdminGrantablePermissionSchema = z.enum(
  ADMIN_API_KEY_GRANTABLE_PERMISSIONS as unknown as [Permission, ...Permission[]],
);

// The owner member an admin-issued key will act as, resolved read-only from the
// selected workspace (never chosen by the operator). `name` is null when the
// resolved member's user carries no name.
export const AdminApiKeyOwnerSchema = z.object({
  memberId: z.uuid(),
  email: z.string(),
  name: z.string().nullable(),
});
export type AdminApiKeyOwner = z.infer<typeof AdminApiKeyOwnerSchema>;

export const ResolveAdminApiKeyOwnerPathParamsSchema = z.object({
  workspaceId: z.uuid(),
});

// `owner` is null when the workspace has no active member holding workspace-wide
// access — the UI shows the no-eligible-owner error and keeps submit disabled.
export const ResolveAdminApiKeyOwnerResponseSchema = z.object({
  owner: AdminApiKeyOwnerSchema.nullable(),
});
export type ResolveAdminApiKeyOwnerResponse = z.infer<typeof ResolveAdminApiKeyOwnerResponseSchema>;

// Mirrors the customer `IssueApiKeyBodySchema` bounds (name 1–80 trimmed,
// description ≤500, rate limit 1–60 default 50 per `API_KEY_RATE_LIMIT_WINDOW_MS`)
// but carries the target `workspaceId` and validates `grantedPermissions`
// against the admin catalogue.
export const IssueAdminApiKeyBodySchema = z.object({
  workspaceId: z.uuid(),
  name: z.string().trim().min(1).max(80),
  description: z.string().trim().max(500).optional(),
  // Requests per `API_KEY_RATE_LIMIT_WINDOW_MS`.
  rateLimit: z.number().int().min(1).max(60).default(50),
  grantedPermissions: z.array(AdminGrantablePermissionSchema).min(1),
});
export type IssueAdminApiKeyBody = z.infer<typeof IssueAdminApiKeyBodySchema>;

// The only surface that ever carries the plaintext `secret` — the admin detail
// item (incl. the issuing operator) plus the write-once secret, rendered once at
// issuance and never persisted in retrievable form.
export const IssueAdminApiKeyResponseSchema = AdminApiKeyDetailSchema.extend({
  secret: z.string(),
});
export type IssueAdminApiKeyResponse = z.infer<typeof IssueAdminApiKeyResponseSchema>;

// --- admin API key issuance (IMM-556.1) --------------------------------------

// A admin API key carries NO permission picker: it holds whatever its bound
// operator holds, resolved at call time. That is the deliberate consequence of
// `ADMIN_PERMISSIONS` being a hardcoded constant today — every active admin
// holds every back-office permission, so a per-key subset is the only place
// granularity could have lived, and the slice chose not to invent it.
//
// `rateLimit` defaults to 30 rather than the workspace key's 50: a polling
// consumer is a different traffic shape from a partner push. Same 1–60 bound.
// `scope` is required with no default — an omitted scope is a 400, never a
// silently-widened key (BR 4).
export const IssueAdminKeyBodySchema = z.object({
  name: z.string().trim().min(1).max(80),
  description: z.string().trim().max(500).optional(),
  // Requests per `API_KEY_RATE_LIMIT_WINDOW_MS`.
  rateLimit: z.number().int().min(1).max(60).default(30),
  scope: AdminKeyScopeSchema,
});
export type IssueAdminKeyBody = z.infer<typeof IssueAdminKeyBodySchema>;

// The only surface that ever carries an admin API key's plaintext `secret`,
// rendered once at issuance and never persisted in retrievable form.
export const IssueAdminKeyResponseSchema = AdminApiKeyDetailSchema.extend({
  secret: z.string(),
});
export type IssueAdminKeyResponse = z.infer<typeof IssueAdminKeyResponseSchema>;

// --- Admin in-place edit (IMM-493.1) ---------------------------------------

// The key to edit is addressed by id alone (cross-tenant), like revocation.
export const UpdateAdminApiKeyPathParamsSchema = z.object({
  id: z.uuid(),
});

// A full REPLACE of the two mutable fields, not a partial patch — both are
// required, matching the two shipped admin PATCH endpoints (`adminWorkspaces.update`,
// `adminMembers.update`). `grantedPermissions` is validated against the admin
// catalogue exactly as issuance is, so editing can never become a looser path
// than issuing: a key-management literal, a `backoffice:*` literal or an unknown
// identifier is rejected at the boundary (400) before the handler runs.
//
// `rateLimit` deliberately carries NO `.default(50)`. A default on an update
// would silently rewrite a field the caller never mentioned; the issue body's
// default is correct only because issuance has no prior value.
export const UpdateAdminApiKeyBodySchema = z.object({
  rateLimit: z.number().int().min(1).max(60),
  grantedPermissions: z.array(AdminGrantablePermissionSchema).min(1),
});
export type UpdateAdminApiKeyBody = z.infer<typeof UpdateAdminApiKeyBodySchema>;

// Returns the same admin detail item `get` and `revoke` return, so the UI
// reflects the new state without a follow-up read. The secret is never
// re-minted and never appears here.
export const UpdateAdminApiKeyResponseSchema = AdminApiKeyDetailSchema;
export type UpdateAdminApiKeyResponse = z.infer<typeof UpdateAdminApiKeyResponseSchema>;

// --- Admin revocation (IMM-435.2) ------------------------------------------

// The key to revoke is addressed by id alone (cross-tenant): the operator acts
// from the admin tier, which carries no workspace scope.
export const RevokeAdminApiKeyPathParamsSchema = z.object({
  id: z.uuid(),
});

// Revocation returns the same admin detail item as `get` — now carrying the
// recorded operator in `revokedByAdmin` — so the UI can reflect the new state
// without a follow-up read.
export const RevokeAdminApiKeyResponseSchema = AdminApiKeyDetailSchema;
export type RevokeAdminApiKeyResponse = z.infer<typeof RevokeAdminApiKeyResponseSchema>;

// --- Requests (nested under a key) -----------------------------------------

// HTTP method captured on the request (Fields table: GET | POST | PUT | PATCH | DELETE).
export const ApiKeyRequestMethodSchema = z.enum(['GET', 'POST', 'PUT', 'PATCH', 'DELETE']);
export type ApiKeyRequestMethod = z.infer<typeof ApiKeyRequestMethodSchema>;

// Coarse outcome classification — the six values IMM-414.1 stores. Kept in sync
// with the backend `ApiKeyRequestOutcome` domain type by hand (the contract
// package cannot import backend code).
export const ApiKeyRequestOutcomeSchema = z.enum([
  'success',
  'denied',
  'rate-limited',
  'client-error',
  'server-error',
  'auth-failed',
]);
export type ApiKeyRequestOutcomeDto = z.infer<typeof ApiKeyRequestOutcomeSchema>;

// One row on the request trail — every captured metadata field plus the joined
// friendly labels, and NEVER the bodies. `workspaceName` / `apiKeyName` are null
// when the referenced workspace / key was since deleted or never resolved (a
// failed-auth request); the UI falls back to the captured id (AC 21).
// One replay attempt. `statusCode` / `outcome` are null while in flight.
export const ApiKeyRequestReplayAttemptSchema = z.object({
  id: z.uuid(),
  attemptedAt: z.iso.datetime(),
  statusCode: z.number().int().nullable(),
  outcome: z.enum(['success', 'client-error', 'server-error']).nullable(),
});
export type ApiKeyRequestReplayAttempt = z.infer<typeof ApiKeyRequestReplayAttemptSchema>;

export const ApiKeyRequestListItemSchema = z.object({
  id: z.uuid(),
  requestId: z.string(),
  workspaceId: z.uuid().nullable(),
  workspaceName: z.string().nullable(),
  apiKeyId: z.uuid().nullable(),
  apiKeyName: z.string().nullable(),
  memberId: z.uuid().nullable(),
  method: ApiKeyRequestMethodSchema,
  endpoint: z.string(),
  permission: z.string().nullable(),
  statusCode: z.number().int(),
  outcome: ApiKeyRequestOutcomeSchema,
  durationMs: z.number().int(),
  clientIp: z.string().nullable(),
  userAgent: z.string().nullable(),
  occurredAt: z.iso.datetime(),
  // The newest replay attempt, or null when never replayed (IMM-458). On the
  // LIST because recovery is worked as a queue: an operator going through a
  // backlog needs the last-run status per row without opening each one. The
  // fields above stay the ORIGINAL call, so this is the only thing that moves.
  lastReplay: ApiKeyRequestReplayAttemptSchema.nullable(),
});
export type ApiKeyRequestListItem = z.infer<typeof ApiKeyRequestListItemSchema>;

// Detail = list item plus the two stored bodies (AC 19). Bodies are captured as
// text (JSON payloads, already credential-stripped and 64 KB-capped by 414.1);
// null when absent (e.g. a 204 response, or a request with no body).
// --- Replay eligibility (IMM-458) -------------------------------------------
//
// Replay re-submits a rejected partner request from its audit trail, so a push
// we wrongly refused can be recovered without asking the partner to resend it.
//
// Only requests the trail can faithfully reconstruct are eligible. The trail
// deliberately stores the matched ROUTE TEMPLATE rather than the raw URL, so an
// endpoint qualifies only when every path param it needs is held in its own
// column — today that means `:workspaceId`, and therefore the lead push.
export const API_KEY_REQUEST_REPLAY_BLOCKED_REASONS = [
  // The original call succeeded — nothing to recover, and replaying would
  // create a second copy of something that already exists.
  'NOT_A_FAILURE',
  // The endpoint's path params cannot be reconstructed from the trail.
  'ENDPOINT_NOT_REPLAYABLE',
  // No stored body to resend (absent, or dropped as unserialisable).
  'NO_REQUEST_BODY',
  // The body exceeded the 64 KB capture cap, so what we hold is a prefix —
  // resending it would submit a truncated, wrong payload.
  'REQUEST_BODY_TRUNCATED',
  // The issuing key has been revoked; replaying would exercise access the
  // workspace has deliberately withdrawn.
  'API_KEY_REVOKED',
  // Already re-submitted. This is the guard that actually prevents duplicates:
  // create-lead's idempotency key is `(apiKeyId, partnerLeadId)`, which does
  // nothing for a partner that never sent a `partnerLeadId`, so without this a
  // second click would create a second lead.
  'ALREADY_REPLAYED',
  // The key no longer holds the permission the original call required, so
  // replaying would exercise access that has since been narrowed.
  'PERMISSION_NO_LONGER_HELD',
] as const;
export const ApiKeyRequestReplayBlockedReasonSchema = z.enum(API_KEY_REQUEST_REPLAY_BLOCKED_REASONS);
export type ApiKeyRequestReplayBlockedReason = z.infer<typeof ApiKeyRequestReplayBlockedReasonSchema>;

export const ApiKeyRequestDetailSchema = ApiKeyRequestListItemSchema.extend({
  requestBody: z.string().nullable(),
  responseBody: z.string().nullable(),
  // Whether this entry can be re-submitted (IMM-458), resolved server-side so
  // the admin app never re-implements the eligibility rule. `false` always
  // comes with a reason; `true` always with `null`.
  replayable: z.boolean(),
  replayBlockedReason: ApiKeyRequestReplayBlockedReasonSchema.nullable(),
  // Every replay attempt against this call, oldest first; empty when never
  // replayed. `statusCode` / `outcome` / `responseBody` above always describe
  // the ORIGINAL call and are never rewritten — the recovery lives here.
  replays: z.array(ApiKeyRequestReplayAttemptSchema),
});
export type ApiKeyRequestDetail = z.infer<typeof ApiKeyRequestDetailSchema>;

// Structured filters for the cross-key request trail (IMM-494.2) — all optional,
// AND-combined. Nothing is scoped from the path: an omitted `workspaceId` /
// `apiKeyId` means every workspace / every key, which is what makes a request
// whose credential never resolved reachable at all. `from` / `to` bound
// `occurredAt`; `limit` defaults to 50 (1–100); `cursor` is the opaque keyset
// token echoed from a previous page's `nextCursor`.
//
// The `*Unresolved` flags are the NULL branch the id filters cannot express:
// `eq(column, value)` is NULL-false in SQL, so a request logged without a
// workspace or key is invisible to every id filter. They select exactly those
// rows — the auth-failed traffic IMM-446 started capturing and nothing has been
// able to show since.
export const ListApiKeyRequestsQuerySchema = z
  .object({
    workspaceId: z.uuid().optional(),
    workspaceUnresolved: z.coerce.boolean().optional(),
    apiKeyId: z.uuid().optional(),
    apiKeyUnresolved: z.coerce.boolean().optional(),
    method: ApiKeyRequestMethodSchema.optional(),
    outcome: ApiKeyRequestOutcomeSchema.optional(),
    endpoint: z.string().min(1).max(200).optional(),
    from: z.iso.datetime().optional(),
    to: z.iso.datetime().optional(),
    limit: z.coerce.number().int().min(1).max(100).optional(),
    cursor: z.string().optional(),
  })
  // Pinning a dimension to an id and to "unresolved" at once asks for rows that
  // are both, which is empty by construction. Only a broken client can send it,
  // so it is a malformed request rather than a business rule — the platform's
  // zod envelope reports it and no error code is minted for it.
  .refine((query) => !(query.workspaceId !== undefined && query.workspaceUnresolved === true), {
    message: 'workspaceId and workspaceUnresolved are mutually exclusive',
    path: ['workspaceUnresolved'],
  })
  .refine((query) => !(query.apiKeyId !== undefined && query.apiKeyUnresolved === true), {
    message: 'apiKeyId and apiKeyUnresolved are mutually exclusive',
    path: ['apiKeyUnresolved'],
  });
export type ListApiKeyRequestsQuery = z.infer<typeof ListApiKeyRequestsQuerySchema>;

export const ListApiKeyRequestsResponseSchema = z.object({
  data: z.array(ApiKeyRequestListItemSchema),
  // Pass back as `?cursor=` for the next page; null when the list is exhausted.
  nextCursor: z.string().nullable(),
});
export type ListApiKeyRequestsResponse = z.infer<typeof ListApiKeyRequestsResponseSchema>;

// One canonical address per entry: an id is an id. The key-scoped form is
// retired (IMM-494.2) — it could never address a request whose key did not
// resolve, because the ownership check compared against a NULL column.
export const GetApiKeyRequestPathParamsSchema = z.object({
  requestId: z.uuid(),
});

export const GetApiKeyRequestResponseSchema = ApiKeyRequestDetailSchema;
export type GetApiKeyRequestResponse = z.infer<typeof GetApiKeyRequestResponseSchema>;

// Replay stays key-scoped (IMM-494.2): re-submitting runs as the original API
// key, so it needs one. It no longer aliases the read's path params, which are
// now key-free — an entry whose key never resolved is readable but, by the same
// token, never replayable (`replayBlockedReason` returns API_KEY_REVOKED).
export const ReplayApiKeyRequestPathParamsSchema = z.object({
  id: z.uuid(),
  requestId: z.uuid(),
});

export const ReplayApiKeyRequestResponseSchema = z.object({
  // The status the replayed call returned. 200 means the lead now exists.
  statusCode: z.number().int(),
  // True when the replay succeeded. A replay that fails again (the payload was
  // genuinely invalid, not a victim of the contract break) reports false and
  // carries the fresh error body so the operator can see why.
  succeeded: z.boolean(),
  // The replayed call's response body, verbatim.
  responseBody: z.string().nullable(),
});
export type ReplayApiKeyRequestResponse = z.infer<typeof ReplayApiKeyRequestResponseSchema>;
